import { generateKeyBetween, generateNKeysBetween } from "fractional-indexing";

import { columnWidth as fallbackColumnWidth } from "../../webapp/globals";
import { useGridSettingsSelect } from "./index";

export type InsertPosition = "last" | "first" | "right_of_current";

export const DEFAULT_INSERT_PAGE_POSITION: InsertPosition = "last";
export const DEFAULT_INSERT_COLUMN_POSITION: InsertPosition = "right_of_current";
export const DEFAULT_CURVE_COLUMN_WIDTH = fallbackColumnWidth;

export const INSERT_POSITION_OPTIONS: { value: InsertPosition; label: string }[] = [
  { value: "last", label: "Last" },
  { value: "first", label: "First" },
  { value: "right_of_current", label: "Right of current tab" },
];

export const INSERT_COLUMN_POSITION_OPTIONS: { value: InsertPosition; label: string }[] = [
  { value: "last", label: "Last" },
  { value: "first", label: "First" },
  { value: "right_of_current", label: "Right of current column" },
];

type IndexedItem = { idx: string };

export function parseInsertPosition(value: string | null | undefined, fallback: InsertPosition): InsertPosition {
  if (value === "last" || value === "first" || value === "right_of_current") return value;
  return fallback;
}

export function getColumnId(item: IndexedItem & { grid_id?: string | null; id?: string | null }) {
  return item.grid_id ?? item.id ?? "";
}

export function computePageInsertIdx<T extends IndexedItem & { id: string }>(
  sorted: T[],
  position: InsertPosition,
  currentPageId?: string | null,
): string {
  const pages = sorted.filter((page) => page.idx && page.idx !== "placeholder");

  if (pages.length === 0) return generateKeyBetween(null, null);

  const appendAfter = (idx: string) => {
    try {
      return generateKeyBetween(idx, null);
    } catch {
      return generateKeyBetween(null, null);
    }
  };

  const insertBefore = (idx: string) => {
    try {
      return generateKeyBetween(null, idx);
    } catch {
      return generateKeyBetween(null, null);
    }
  };

  const insertBetween = (leftIdx: string, rightIdx: string | null | undefined) => {
    try {
      return generateKeyBetween(leftIdx, rightIdx ?? null);
    } catch {
      return appendAfter(leftIdx);
    }
  };

  if (position === "first") {
    return insertBefore(pages[0].idx);
  }

  if (position === "last") {
    return appendAfter(pages[pages.length - 1].idx);
  }

  const currentIndex = currentPageId ? pages.findIndex((page) => page.id === currentPageId) : -1;
  const anchorIndex = currentIndex >= 0 ? currentIndex : pages.length - 1;

  return insertBetween(pages[anchorIndex].idx, pages[anchorIndex + 1]?.idx);
}

/**
 * MAIN month is pinned left of all page-product columns and is not itself a
 * page_product. "Right of current column" from MAIN month means insert first.
 */
export function effectiveColumnInsertPosition(position: InsertPosition, fromMainMonth: boolean): InsertPosition {
  if (fromMainMonth && position === "right_of_current") return "first";
  return position;
}

export function computeColumnInsertIdxs<T extends IndexedItem & { grid_id?: string | null; id?: string | null }>(
  sorted: T[],
  count: number,
  position: InsertPosition,
  currentColumnId?: string | null,
): string[] {
  if (sorted.length === 0) {
    return generateNKeysBetween(null, null, count);
  }

  if (position === "first") {
    return generateNKeysBetween(null, sorted[0].idx, count);
  }

  if (position === "last") {
    return generateNKeysBetween(sorted[sorted.length - 1].idx, null, count);
  }

  const currentIndex = currentColumnId ? sorted.findIndex((column) => getColumnId(column) === currentColumnId) : -1;
  const anchorIndex = currentIndex >= 0 ? currentIndex : sorted.length - 1;

  return generateNKeysBetween(sorted[anchorIndex].idx, sorted[anchorIndex + 1]?.idx, count);
}

export const useInsertPagePosition = () =>
  useGridSettingsSelect((settings) =>
    parseInsertPosition(settings?.insert_page_position, DEFAULT_INSERT_PAGE_POSITION),
  );

export const useInsertColumnPosition = () =>
  useGridSettingsSelect((settings) =>
    parseInsertPosition(settings?.insert_column_position, DEFAULT_INSERT_COLUMN_POSITION),
  );

export const useDefaultCurveColumnWidth = () =>
  useGridSettingsSelect((settings) => settings?.default_curve_column_width ?? DEFAULT_CURVE_COLUMN_WIDTH);
